import React, { useState, useRef, useEffect } from 'react';
import styles from './QuickFilterDropdown.module.css';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { matchesSearch } from './searchText';

/**
 * QuickFilterDropdown
 * Dropdown compacto de seleção múltipla para filtros rápidos.
 *
 * Props:
 *  - label: string — nome do filtro (ex: "Profissionais")
 *  - options: string[] — lista de opções
 *  - selected: number[] — índices selecionados
 *  - onChange: (indices: number[]) => void
 *  - allLabel: string — texto quando tudo selecionado (ex: "Todos")
 *  - searchable: boolean — campo de busca no topo (listas longas, ex.: muitas agendas);
 *    Enter marca/desmarca a primeira opção visível, Esc fecha
 */
const QuickFilterDropdown = ({ label, options, selected, onChange, allLabel = 'Todos', searchable = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  // Fecha ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cada abertura começa com a busca limpa
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // Opções visíveis (índice original preservado, porque `selected` guarda índices)
  const visibleOptions = options
    .map((opt, i) => ({ opt, i }))
    .filter(({ opt }) => !searchable || matchesSearch(opt, query));

  const allSelected = selected.length === options.length;
  const noneSelected = selected.length === 0;

  const valueLabel = allSelected || noneSelected
    ? allLabel
    : selected.length === 1
      ? options[selected[0]]
      : `${selected.length} selecionados`;

  // Toggle simples: marca/desmarca sem forçar "Todos"
  const toggleOption = (index) => {
    if (selected.includes(index)) {
      // Não deixa desmarcar o último — mantém pelo menos 1
      if (selected.length === 1) return;
      onChange(selected.filter(i => i !== index));
    } else {
      onChange([...selected, index]);
    }
  };

  // "Todos" sempre reseta para todos selecionados
  const toggleAll = () => {
    onChange(options.map((_, i) => i));
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (visibleOptions[0]) toggleOption(visibleOptions[0].i);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        onClick={() => setOpen(!open)}
      >
        <span className={styles.triggerLabel}>{label}</span>
        <span className={styles.triggerValue}>
          {valueLabel}
          <ExpandMoreOutlinedIcon
            sx={{ fontSize: 14, transition: 'transform 0.15s ease', transform: open ? 'rotate(180deg)' : 'none' }}
          />
        </span>
      </button>

      {open && (
        <div className={styles.dropdown}>
          {searchable && (
            <div className={styles.searchRow}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Buscar agenda"
                aria-label={`Buscar em ${label}`}
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
          )}

          {/* Opção "Todos" */}
          <button
            type="button"
            className={`${styles.option} ${allSelected ? styles.optionActive : ''}`}
            onClick={toggleAll}
          >
            <span className={styles.optionCheck}>
              {allSelected && <CheckOutlinedIcon sx={{ fontSize: 11 }} />}
            </span>
            <span className={styles.optionLabel}>{allLabel}</span>
          </button>

          <div className={styles.divider} />

          {searchable && visibleOptions.length === 0 && (
            <div className={styles.searchEmpty}>Nenhuma agenda com esse nome</div>
          )}

          {visibleOptions.map(({ opt, i }) => (
            <button
              key={i}
              type="button"
              className={`${styles.option} ${selected.includes(i) ? styles.optionActive : ''}`}
              onClick={() => toggleOption(i)}
            >
              <span className={styles.optionCheck}>
                {selected.includes(i) && <CheckOutlinedIcon sx={{ fontSize: 11 }} />}
              </span>
              <span className={styles.optionLabel}>{opt}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuickFilterDropdown;
